import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, PlayCircle, Coins, Sparkles, CreditCard, ChevronDown,
  MessageCircleQuestion, ArrowRight, ShieldCheck,
} from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { useCredits } from "@/context/CreditsContext";
import { HELP } from "@/constants/testIds";
import content from "@/data/help-center-content.json";
import { helpApi } from "@/lib/api";

// Feature flags — hide FAQ articles for not-yet-shipped features (featureGate).
const FEATURES = {
  welcomeCredits: true,
  aiText: true, // Etap 2A wdrożone: podpowiedzi pól, „Popraw tekst", generowanie tekstu
  aiImage: false, // Etap 2B — wkrótce
  fileAnalysis: false, // Etap 2B — wkrótce
  autoTopUp: false, // Etap 4
  billingDocuments: false,
};

const ICONS = { "play-circle": PlayCircle, coins: Coins, sparkles: Sparkles, "credit-card": CreditCard };

// Polish-aware normalization for search (strip diacritics incl. ł).
const norm = (s = "") =>
  s
    .toLowerCase()
    .replace(/ł/g, "l")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

function gateOk(a) {
  return !a.featureGate || FEATURES[a.featureGate];
}

function articleText(a) {
  return norm([a.title, a.shortAnswer, (a.body || []).join(" "), (a.keywords || []).join(" ")].join(" "));
}

function ArticleItem({ article, open, onToggle, focusRef, onOpenAction, onOpenRelated }) {
  return (
    <div id={article.id} data-testid={HELP.article} className="rounded-field border border-brand-line bg-brand-surface">
      <button
        type="button"
        ref={focusRef}
        data-testid={HELP.articleToggle}
        aria-expanded={open}
        aria-controls={`${article.id}-panel`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
      >
        <span className="min-w-0">
          <span className="block font-medium text-brand-text">{article.title}</span>
          {!open && article.shortAnswer && (
            <span className="mt-0.5 block truncate text-sm text-brand-muted">{article.shortAnswer}</span>
          )}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-brand-sec transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div id={`${article.id}-panel`} className="border-t border-brand-line px-5 py-4 text-sm text-brand-sec">
          {(article.body || []).map((p, i) => (
            <p key={i} className={i ? "mt-2" : ""}>{p}</p>
          ))}
          {article.steps && (
            <ol className="mt-3 list-decimal space-y-1.5 pl-5">
              {article.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
          {article.action && (
            <button
              type="button"
              onClick={() => onOpenAction(article.action.url)}
              className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on"
            >
              {article.action.label} <ArrowRight size={15} />
            </button>
          )}
          {article.relatedArticleIds?.length > 0 && (
            <div className="mt-4 border-t border-brand-line pt-3">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted">Powiązane pytania</p>
              <div className="flex flex-wrap gap-2">
                {article.relatedArticleIds.map((rid) => (
                  <button
                    key={rid}
                    type="button"
                    onClick={() => onOpenRelated(rid)}
                    className="rounded-full border border-brand-line px-3 py-1 text-xs text-brand-sec transition-colors hover:border-brand-accent/50 hover:text-brand-text"
                  >
                    {rid.replace(/-/g, " ")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HelpCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { openBuy } = useCredits();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [open, setOpen] = useState({});
  const [feed, setFeed] = useState(content);
  const refs = useRef({});

  useEffect(() => {
    helpApi.faq().then((d) => { if (d?.categories?.length) setFeed({ ...content, categories: d.categories }); }).catch(() => {});
  }, []);

  const allArticles = useMemo(
    () =>
      feed.categories.flatMap((c) =>
        c.articles.filter(gateOk).map((a) => ({ ...a, categoryId: c.id, categoryTitle: c.title })),
      ),
    [feed],
  );

  const resolveUrl = (url) => {
    if (!url) return;
    if (url === "/kup-kredyty") return openBuy();
    const alias = { "/konto/kredyty": "/konto", "/konto/historia-kredytow": "/konto" };
    navigate(alias[url] || url);
  };

  const openArticle = (id) => {
    setOpen((o) => ({ ...o, [id]: true }));
    setCategory("all");
    setQuery("");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      refs.current[id]?.focus();
    }, 80);
  };

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && allArticles.some((a) => a.id === hash)) {
      setOpen({ [hash]: true });
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "center" });
        refs.current[hash]?.focus();
      }, 150);
    }
  }, [location.hash, allArticles]);

  const q = norm(query.trim());
  const searching = q.length > 0;
  const visible = allArticles.filter((a) => {
    if (searching) return articleText(a).includes(q);
    return category === "all" || a.categoryId === category;
  });

  const selectCategory = (id) => {
    setCategory(id);
    setQuery("");
    if (id === "all") setParams({});
    else setParams({ category: id });
  };

  return (
    <AppShell>
      <div data-testid={HELP.root} className="mx-auto max-w-4xl">
        <header className="mb-7">
          <h1 className="font-display text-3xl font-semibold text-brand-text sm:text-4xl">{content.title}</h1>
          <p className="mt-3 max-w-2xl text-brand-sec">{content.lead}</p>
        </header>

        <div className="relative mb-7">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="search"
            data-testid={HELP.search}
            aria-label="Wyszukaj temat pomocy"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={content.search.placeholder}
            className="w-full rounded-field border border-brand-line bg-brand-surface py-3.5 pl-11 pr-4 text-[15px] text-brand-text placeholder:text-brand-muted outline-none focus:border-brand-accent"
          />
        </div>

        {!searching && (
          <>
            {/* Quick actions */}
            <div className="mb-7 grid gap-4 sm:grid-cols-2">
              {content.quickActions.map((qa) => {
                const Icon = ICONS[qa.icon] || Sparkles;
                return (
                  <button
                    key={qa.id}
                    type="button"
                    data-testid={qa.id === "prompt-matcher" ? HELP.matcherCta : HELP.quickAction}
                    onClick={() => (qa.url ? resolveUrl(qa.url) : openArticle(qa.articleId))}
                    className="group flex flex-col items-start rounded-panel border border-brand-line bg-brand-surface p-5 text-left transition-colors hover:border-brand-accent/50"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                      <Icon size={20} strokeWidth={1.75} />
                    </span>
                    <h3 className="mt-3 font-display text-base font-semibold text-brand-text">{qa.title}</h3>
                    <p className="mt-1 text-sm text-brand-sec">{qa.description}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-accent">
                      {qa.label} <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Category chips */}
            <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Kategorie pomocy">
              {[{ id: "all", title: "Wszystkie" }, ...feed.categories].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={category === c.id}
                  data-testid={HELP.categoryChip}
                  onClick={() => selectCategory(c.id)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    category === c.id
                      ? "border-brand-accent bg-brand-accent/10 text-brand-accent"
                      : "border-brand-line text-brand-sec hover:border-brand-accent/50 hover:text-brand-text"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Articles / results */}
        {searching && visible.length === 0 ? (
          <div data-testid={HELP.searchEmpty} className="rounded-panel border border-brand-line bg-brand-surface p-8 text-center">
            <MessageCircleQuestion className="mx-auto mb-3 text-brand-muted" size={28} />
            <p className="text-brand-sec">{content.search.emptyResult}</p>
            <p className="mt-1 text-sm text-brand-muted">{content.search.emptyResultHint}</p>
            <Link
              to={content.search.emptyResultActionUrl}
              data-testid={HELP.searchEmptyCta}
              className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"
            >
              {content.search.emptyResultActionLabel} <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {searching && <p className="text-sm text-brand-muted">Znaleziono {visible.length}</p>}
            {visible.map((a) => (
              <ArticleItem
                key={a.id}
                article={a}
                open={!!open[a.id]}
                onToggle={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                focusRef={(el) => (refs.current[a.id] = el)}
                onOpenAction={resolveUrl}
                onOpenRelated={openArticle}
              />
            ))}
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 flex flex-col items-start gap-4 rounded-panel border border-brand-accent/30 bg-brand-surface p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-brand-text">{content.contact.title}</h2>
            <p className="mt-1 text-sm text-brand-sec">{content.contact.description}</p>
          </div>
          <Link
            to={content.contact.url}
            data-testid={HELP.contactCta}
            className="inline-flex shrink-0 items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
          >
            {content.contact.label} <ArrowRight size={16} />
          </Link>
        </div>

        {/* Legal links */}
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-brand-line pt-6 text-sm">
          <span className="flex items-center gap-1.5 text-brand-muted"><ShieldCheck size={14} /> Dokumenty:</span>
          {content.legalLinks.map((l) => (
            <Link key={l.url} to={l.url} data-testid={HELP.legalLink} className="text-brand-sec hover:text-brand-accent">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
